import { ReactNode, useContext, useEffect, useState } from 'react';
import { Tenant } from 'models/tenant';
import { useTenantDataService } from 'services/rest-api.service';
import { Auth0Context } from '@auth0/auth0-react';
import { BooleanParam, StringParam, useQueryParam } from 'use-query-params';
import { useTokenPermissions } from 'hooks/useTokenPermissions';
import { FullscreenLoadingSpinner } from 'components/FullscreenLoadingSpinner';
import { API, BENCHMARKING, INSIGHTS, TenantConfig, TenantConfigContext } from './TenantConfigContext';

export function TenantConfigProvider({ children }: { children: ReactNode }) {
  const [tenant, setTenant] = useState<Tenant>();

  const authContext = useContext(Auth0Context);

  const loadCurrentTenant = useTenantDataService().getAll;

  const [isAdminRole, setAdminRole] = useState<boolean>(false);
  const [isSuperAdmin, setSuperAdmin] = useState<boolean>(false);
  const [isSupportAdmin, setSupportAdmin] = useState<boolean>(false);
  const [isAdminResolved, setAdminResolved] = useState<boolean>(false);
  const [tokenPermissions] = useTokenPermissions();

  useEffect(() => {
    const permsString = tokenPermissions.toString();
    const hasSuperAdmin = permsString.includes('tb360:super_admin');
    const hasSupportAdmin = permsString.includes('read:all_accounts');

    setSuperAdmin(hasSuperAdmin);
    setSupportAdmin(hasSupportAdmin);
    setAdminRole(hasSuperAdmin || hasSupportAdmin);
    setAdminResolved(true);
  }, [tokenPermissions]);

  const [accountNumber, setAccountNumber] = useQueryParam('accountNumber', StringParam);

  const [adminView, setAdminView] = useQueryParam('adminView', BooleanParam);

  const loadTenant = () => {
    loadCurrentTenant().then(response => {
      if (response.error) {
        throw new Error(response.error.message);
      }
      setTenant(response.data ?? undefined);
    });
  };

  const safeDataVersion = (serverDataVersionString: string | undefined): Date | undefined => {
    if (!serverDataVersionString) return undefined;
    try {
      return new Date(parseInt(serverDataVersionString, 10));
    } catch {
      return undefined;
    }
  };

  const getFeatureFlags = (): string[] => {
    return tenant?.featureFlags ?? [];
  };

  const getPermissions = (isAdminView: boolean): string[] => {
    if (!isAdminView) {
      return tenant?.customerPermissions ?? [];
    }

    if (isSuperAdmin) {
      return [INSIGHTS, BENCHMARKING, API];
    }

    if (isSupportAdmin) {
      return [INSIGHTS, BENCHMARKING];
    }

    return tenant?.permissions ?? [];
  };

  const createTenantConfig = (): TenantConfig => ({
    name: tenant?.name ?? '',
    accountNumber: tenant?.accountNumber ?? accountNumber ?? '',
    isAdminView: isAdminRole && (adminView ?? true),
    isSuperAdmin,
    isSupportAdmin,
    isAdminResolved,
    onboardingStatus: tenant?.onboardingStatus ?? 'ACTIVATED',
    testTenantFlag: tenant?.testTenantFlag ?? false,
    currentDataVersion: safeDataVersion(tenant?.currentDataVersion),
    currencyCode: 'EUR',
    featureFlags: getFeatureFlags(),
    permissions: getPermissions(isAdminRole && (adminView ?? true)),
    updateActivationStatus: newStatus => {
      if (tenant) {
        setTenant({
          ...tenant,
          onboardingStatus: newStatus,
        });
      }
    },
    setActiveAccountNumber: activeAccountNumber => {
      if (tenant) {
        setAccountNumber(activeAccountNumber);
      }
    },
    setAdminView: () => {
      // placeholder set below once we have context
    },
    reloadTenant: () => {
      loadTenant();
    },
  });

  const [context, setContext] = useState<TenantConfig>(createTenantConfig());

  context.setAdminView = (newAdminViewState: boolean) => {
    const safeIsAdminView = isAdminRole && newAdminViewState;
    setAdminView(safeIsAdminView ? undefined : false);
    setContext({
      ...context,
      isAdminView: safeIsAdminView,
      isSuperAdmin,
      isSupportAdmin,
      isAdminResolved,
      featureFlags: getFeatureFlags(),
      permissions: getPermissions(safeIsAdminView),
    });
  };

  const { isAuthenticated, isLoading } = authContext;

  useEffect(() => {
    // Needed as otherwise the call to loadTenant is failing, causing a reload, causing another authentication ....
    if (isAuthenticated && !isLoading) {
      loadTenant();
    }
  }, [isAuthenticated, isLoading, accountNumber]);

  useEffect(() => {
    setContext(createTenantConfig());
  }, [tenant]);

  if (isLoading) {
    return <FullscreenLoadingSpinner />;
  }

  return <TenantConfigContext.Provider value={context}>{children}</TenantConfigContext.Provider>;
}
